import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { map, get, head, groupBy, values, isEmpty } from 'lodash'
import cn from 'classnames'

import { ReactComponent as IconCaret } from 'assets/icons/IconCaret.svg'
import { ReactComponent as IconClose } from 'assets/icons/IconClose.svg'

const Autocomplete = ({
  options,
  label,
  name,
  handleAutocompleteInputChange,
  selectedOption,
  handleAutocompleteDropdownChange,
  isRequired,
}) => {
  const textBoxRef = useRef(null)
  const autocompleteRef = useRef(null)
  const [isListVisible, setListVisible] = useState(false)
  const [isOptionChosen, setOptionChosen] = useState(true)

  const sortedOptionsByLetter = options.sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter))
  const groupedOptions = map(
    groupBy(sortedOptionsByLetter, (option) => option.firstLetter),
    (group) => values(group),
  )

  const handleClickOutside = (e) => {
    if (autocompleteRef.current.contains(e.target)) {
      return
    }
    setListVisible(false)
  }

  useEffect(
    () => {
      if (isListVisible) {
        document.addEventListener('mousedown', handleClickOutside)
        textBoxRef.current.focus()
      } else {
        document.removeEventListener('mousedown', handleClickOutside)
        if (!isOptionChosen) {
          handleAutocompleteDropdownChange(name, [{ label: '', value: '' }])
        }
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    },
    // eslint-disable-next-line
    [isListVisible]
  )

  const clearAutocompleteValue = (e) => {
    e.preventDefault()
    handleAutocompleteDropdownChange(name, [{ label: '', value: '' }])
    setOptionChosen(false)
    setListVisible(true)
  }

  const selectOption = (name, value) => {
    handleAutocompleteDropdownChange(name, value)
    setListVisible(false)
    setOptionChosen(true)
  }

  const changeInputValue = (e) => {
    setOptionChosen(false)
    handleAutocompleteInputChange(e)
  }

  const isSearchVisible =
    isEmpty(selectedOption.label) || (!isEmpty(selectedOption.label) && isListVisible)

  return (
    <div
      className={cn('autocomplete', {
        '-has-value': !isEmpty(selectedOption.label),
      })}
      ref={autocompleteRef}
    >
      {!isEmpty(selectedOption.label) && (
        <ul className="tags-list">
          <li className="tags-list__item">
            <p>{selectedOption.label}</p>
            <button className="tags-list__item__action" onClick={clearAutocompleteValue}>
              <IconClose />
            </button>
          </li>
        </ul>
      )}
      {isSearchVisible && (
        <input
          autoComplete="off"
          className="autocomplete__input"
          name={name}
          onChange={changeInputValue}
          onFocus={() => setListVisible(true)}
          placeholder={label}
          ref={textBoxRef}
          required={isRequired}
        />
      )}
      <button
        className="autocomplete__arrow"
        onClick={() => setListVisible(!isListVisible)}
        type="button"
      >
        <IconCaret className="autocomplete__arrow-icon" />
      </button>
      {isListVisible && (
        <ul className="autocomplete__list">
          {options.length === 0 && <p>No options</p>}
          {map(groupedOptions, (options, index) => (
            <React.Fragment key={index}>
              <header>
                <p className="autocomplete__header">{get(head(options), 'firstLetter')}</p>
              </header>
              {map(options, ({ value, label }) => (
                <li
                  className="autocomplete__item"
                  key={value}
                  onClick={() => selectOption(name, [{ label, value }])}
                >
                  {label}
                </li>
              ))}
            </React.Fragment>
          ))}
        </ul>
      )}
    </div>
  )
}

Autocomplete.defaultProps = {
  isRequired: false,
}

Autocomplete.propTypes = {
  options: PropTypes.array.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string,
  handleAutocompleteInputChange: PropTypes.func,
  handleAutocompleteDropdownChange: PropTypes.func,
  selectedOption: PropTypes.object.isRequired,
  isRequired: PropTypes.bool,
}

export default Autocomplete
